import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Shield, UserCheck, UserX } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, action) => {
    try {
      await axios.post(
        `${API}/admin/users/update`,
        { user_id: userId, action },
        { withCredentials: true }
      );
      toast.success(action === 'restrict' ? 'User access restricted' : 'User access granted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'active') return 'bg-green-500 text-white';
    if (status === 'trial') return 'bg-blue-500 text-white';
    if (status === 'expired') return 'bg-red-500 text-white';
    return 'bg-gray-500 text-white';
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <Shield className="w-6 h-6 text-white" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Admin Panel
            </h1>
          </div>
          <Button
            onClick={() => navigate('/admin/analytics')}
            variant="ghost"
            className="text-white hover:bg-white/20"
          >
            View Analytics
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-12 space-y-8">
        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass border-white/20">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-white/80 text-sm">Total Users</p>
                <p className="text-3xl font-bold text-white mt-2">{users.length}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass border-white/20">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-white/80 text-sm">Active Subscribers</p>
                <p className="text-3xl font-bold text-green-400 mt-2">
                  {users.filter(u => u.subscription_status === 'active').length}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass border-white/20">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-white/80 text-sm">Trial Users</p>
                <p className="text-3xl font-bold text-blue-400 mt-2">
                  {users.filter(u => u.subscription_status === 'trial').length}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass border-white/20">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-white/80 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold text-yellow-400 mt-2">
                  £{users.reduce((sum, u) => sum + (u.total_revenue || 0), 0).toFixed(2)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Management Table */}
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Space Grotesk' }}>
              User Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/20">
                      <TableHead className="text-white">Name</TableHead>
                      <TableHead className="text-white">Email</TableHead>
                      <TableHead className="text-white">Role</TableHead>
                      <TableHead className="text-white">Status</TableHead>
                      <TableHead className="text-white">Trial/Subscription Expiry</TableHead>
                      <TableHead className="text-white">Last Payment</TableHead>
                      <TableHead className="text-white">Total Revenue</TableHead>
                      <TableHead className="text-white">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => {
                      const isTrialExpired = user.subscription_status === 'trial' && 
                        new Date(user.free_trial_ends_at) < new Date();
                      const subscriptionExpiry = user.subscription_expires_at ? 
                        new Date(user.subscription_expires_at) : null;
                      const isSubscriptionExpired = subscriptionExpiry && subscriptionExpiry < new Date();
                      
                      return (
                        <TableRow key={user.id} className="border-white/20" data-testid={`user-row-${user.id}`}>
                          <TableCell className="text-white">{user.name}</TableCell>
                          <TableCell className="text-white text-xs">{user.email}</TableCell>
                          <TableCell className="text-white">
                            <span className={user.role === 'admin' ? 'bg-purple-500 text-white px-2 py-1 rounded text-xs' : 'text-white'}>
                              {user.role}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`${getStatusBadge(user.subscription_status)} px-3 py-1 rounded-full text-xs font-semibold`}>
                              {user.subscription_status}
                            </span>
                          </TableCell>
                          <TableCell className="text-white text-xs">
                            {user.subscription_status === 'trial' ? (
                              <div>
                                <div>{new Date(user.free_trial_ends_at).toLocaleDateString()}</div>
                                {isTrialExpired && <span className="text-red-400 text-xs">Expired</span>}
                              </div>
                            ) : user.subscription_status === 'active' && subscriptionExpiry ? (
                              <div>
                                <div>{subscriptionExpiry.toLocaleDateString()}</div>
                                {isSubscriptionExpired && <span className="text-yellow-400 text-xs">Renewal Due</span>}
                              </div>
                            ) : (
                              <span className="text-white/50">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-white text-xs">
                            {user.last_payment ? (
                              <div>
                                <div>£{user.last_payment.amount.toFixed(2)}</div>
                                <div className="text-white/60 text-xs">
                                  {new Date(user.last_payment.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            ) : (
                              <span className="text-white/50">No payments</span>
                            )}
                          </TableCell>
                          <TableCell className="text-white font-semibold">
                            {user.total_revenue > 0 ? (
                              `£${user.total_revenue.toFixed(2)}`
                            ) : (
                              <span className="text-white/50">£0.00</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {user.role !== 'admin' && (
                              <div className="flex gap-2">
                                {user.subscription_status !== 'expired' ? (
                                  <Button
                                    data-testid={`restrict-user-${user.id}`}
                                    onClick={() => handleUpdateUser(user.id, 'restrict')}
                                    size="sm"
                                    variant="ghost"
                                    className="text-white hover:bg-red-500/20"
                                  >
                                    <UserX className="w-4 h-4 mr-1" />
                                    Restrict
                                  </Button>
                                ) : (
                                  <Button
                                    data-testid={`grant-user-${user.id}`}
                                    onClick={() => handleUpdateUser(user.id, 'grant')}
                                    size="sm"
                                    variant="ghost"
                                    className="text-white hover:bg-green-500/20"
                                  >
                                    <UserCheck className="w-4 h-4 mr-1" />
                                    Grant
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminPanel;
